import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUserLikes } from "../../services/likesService";
import { getPlaceById } from "../../services/placeService";
import { Place } from "../../types/Place";
import { UserLike } from "../../types/UserLike";
import { debugLogError } from "../../utils/debug-log-error";
import ErrorDisplay from "../ErrorDisplay/ErrorDisplay";
import { Headings } from "../Headings";
import LoadingMask from "../LoadingMask/LoadingMask";
import "./UserLikedPlaces.scss";

interface UserLikedPlacesProps {
  userId: string;
}

const UserLikedPlaces: React.FC<UserLikedPlacesProps> = ({ userId }) => {
  const [likedPlaces, setLikedPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [noLikesFound, setNoLikesFound] = useState<boolean>(false);

  const fetchLikedPlaces = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError("User ID is not provided.");
      return;
    }

    setLoading(true);
    setError(null);
    setNoLikesFound(false);

    try {
      const userLikes: UserLike[] = await getUserLikes(userId);

      if (userLikes.length === 0) {
        setNoLikesFound(true);
        setLikedPlaces([]);
        setLoading(false);
        return;
      }

      const placePromises = userLikes
        .filter((like) => like.placeId) // Ensure placeId is present
        .map((like) => getPlaceById(like.placeId));

      const fetchedPlacesResults = await Promise.all(placePromises);

      const validPlaces = fetchedPlacesResults.filter(
        (place): place is Place => place !== null
      );

      if (validPlaces.length === 0 && userLikes.length > 0) {
        // This means likes existed but corresponding places were not found
        setError("Could not fetch details for liked places.");
      }

      setLikedPlaces(validPlaces);
    } catch (err) {
      debugLogError("UserLikedPlaces", err);
      setError("Failed to load liked places. Please try again later.");
      setLikedPlaces([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchLikedPlaces();
  }, [fetchLikedPlaces]);

  if (loading) {
    return <LoadingMask message="Loading liked places..." />;
  }

  const hasLikes = !noLikesFound && likedPlaces.length > 0;
  return (
    <div className="user-liked-places-box user-liked-places">
      <Headings.PhoneSubBox>Your Favorite Places</Headings.PhoneSubBox>
      {error && <ErrorDisplay error={error} />}
      <div className="user-liked-places-list">
        {hasLikes &&
          likedPlaces.map((place) => (
            <Link
              to={`/place/${place.googlePlaceId}`}
              key={place.googlePlaceId}
            >
              {place.name}
            </Link>
          ))}
        {!hasLikes && (
          <div className="alert alert-warning">
            You haven't liked any places yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default UserLikedPlaces;
